// Product page sections like Shein (Benni 3 Nov 2026): "Shop the look" and "You may also like" with View more.
// Everything is fetched from Shopify's own Section Rendering API.

// Fetches a section and returns its product <li> items
async function mystoreFetchItems(url) {
  const response = await fetch(url);
  if (!response.ok) return { items: [], doc: null };
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return { items: [...doc.querySelectorAll('[data-mystore-item]')], doc };
}

// Adds items whose product is not on the page yet (the main product counts too: its variant picker carries
// data-product-id); returns how many were added
function mystoreAppendItems(list, items, limit = Infinity) {
  let added = 0;
  for (const item of items) {
    if (added >= limit) break;
    const id = item.dataset.productId;
    if (!id || document.querySelector(`[data-product-id="${id}"]`)) continue;
    list.appendChild(document.importNode(item, true));
    added++;
  }
  return added;
}

// Which option the shopper just changed; media-gallery.js scrolls up to the photo only for a colour.
// The variant in the cart form switches right away, so Add to cart never waits for Dawn's page update.
document.addEventListener('change', (event) => {
  const selects = event.target.closest('variant-selects');
  if (!selects) return;
  window.mystoreChangedOption = event.target.closest('.product-form__input--image') ? 'color' : 'other';

  let variants = [];
  try {
    variants = JSON.parse(selects.querySelector('[data-mystore-variants]')?.textContent || '[]');
  } catch (e) {}
  const picked = [...selects.querySelectorAll('fieldset, .product-form__input--dropdown')].map(
    (input) => input.querySelector('input:checked, select')?.value
  );
  const variant = variants.find((v) => v.options.every((value, i) => value === picked[i]));
  const form = document.getElementById(event.target.getAttribute('form'));
  if (!variant || !form) return;
  form.querySelectorAll('input[name="id"]').forEach((input) => (input.value = variant.id));
  const atcPrice = form.querySelector('[data-mystore-atc-price]');
  if (atcPrice && typeof variant.price === 'number') {
    atcPrice.textContent = `${Math.floor(variant.price / 100)}${atcPrice.dataset.sep}${String(variant.price % 100).padStart(2, '0')}${atcPrice.dataset.symbol}`;
  }
  const submit = form.querySelector('[name="add"]');
  if (submit) submit.disabled = !variant.available;
});

// Product photos, "Shop the look" and the colour row can be dragged with the mouse like a swipe (Benni 3 Nov 2026): phones swipe
// natively, but a mouse (desktop, the app preview) has no way to move a scroll-snap row
document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  const row = event.target.closest('.product__media-list, .mystore-look__list, .mystore-colors');
  if (!row || row.scrollWidth <= row.clientWidth) return;
  event.preventDefault();
  const startX = event.clientX;
  const item = row.classList.contains('mystore-look__list')
    ? row.firstElementChild
    : row.classList.contains('mystore-colors')
    ? row.querySelector(':scope > label')
    : null;
  const step = item?.getBoundingClientRect().width || row.clientWidth;
  document.addEventListener(
    'pointerup',
    (up) => {
      const moved = up.clientX - startX;
      if (Math.abs(moved) < 30) return;
      row.scrollBy({ left: moved < 0 ? step : -step, behavior: 'smooth' });
      // a drag that ends on a card is not a click on it
      row.addEventListener('click', (click) => click.preventDefault(), { capture: true, once: true });
    },
    { once: true }
  );
});

// Reward bar under the photos: how far the cart is from each step, by cart total or by number of items
if (!customElements.get('mystore-tiers')) {
  customElements.define(
    'mystore-tiers',
    class MystoreTiers extends HTMLElement {
      connectedCallback() {
        this.steps = [...this.querySelectorAll('.mystore-tiers__step')].map((el) => ({
          el,
          type: el.dataset.type,
          goal: Number(el.dataset.goal),
          label: el.dataset.label,
        }));
        this.render({ total: Number(this.dataset.total) || 0, items: Number(this.dataset.items) || 0 });
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      async refresh() {
        try {
          const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
          this.render({ total: cart.total_price || 0, items: cart.item_count || 0 });
        } catch (e) {}
      }

      money(cents) {
        return (cents / 100).toFixed(2).replace('.', this.dataset.sep || '.') + (this.dataset.symbol || '');
      }

      render(cart) {
        const value = (step) => (step.type === 'items' ? cart.items : cart.total);
        const n = this.steps.length;
        // the steps sit at equal distances; the bar fills the next open step by its own measure
        let fill = 1;
        for (let i = 0; i < n; i++) {
          const step = this.steps[i];
          if (value(step) < step.goal) {
            fill = (i + value(step) / step.goal) / n;
            break;
          }
        }
        this.querySelector('[data-tiers-fill]').style.width = `${Math.max(Math.min(fill, 1), 0) * 100}%`;
        this.steps.forEach((step, i) => {
          const reached = value(step) >= step.goal;
          step.el.classList.toggle('is-reached', reached);
          this.querySelectorAll('.mystore-tiers__label')[i]?.classList.toggle('is-reached', reached);
        });

        const next = this.steps.find((step) => value(step) < step.goal);
        const text = this.querySelector('[data-tiers-text]');
        if (!next) {
          text.textContent = this.dataset.done;
          return;
        }
        if (cart.items === 0) return;
        // the amount and the reward stand out in the sentence
        const strong = (content, cls) => {
          const b = document.createElement('b');
          b.className = cls;
          b.textContent = content;
          return b.outerHTML;
        };
        const plain = (content) => {
          const span = document.createElement('span');
          span.textContent = content;
          return span.innerHTML;
        };
        const missing = next.goal - value(next);
        const template = next.type === 'items' ? this.dataset.addItems : this.dataset.addAmount;
        text.innerHTML = plain(template)
          .replace('[amount]', strong(this.money(missing), 'mystore-tiers__amount'))
          .replace('[count]', strong(String(missing), 'mystore-tiers__amount'))
          .replace('[reward]', strong(next.label, 'mystore-tiers__reward'));
      }
    }
  );
}
// Countdown in the offer bar: to a real end date, or Pettypurse's rolling window (each visitor gets the window
// length, kept in this browser, and it starts again when it runs out)
if (!customElements.get('mystore-countdown')) {
  customElements.define(
    'mystore-countdown',
    class MystoreCountdown extends HTMLElement {
      static KEY = 'mystore_countdown_end';

      connectedCallback() {
        this.clock = this.querySelector('[data-clock]');
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      endAt() {
        if (this.dataset.end) return Date.parse(this.dataset.end);
        const windowMs = Number(this.dataset.window) * 1000;
        let end = 0;
        try {
          end = parseInt(localStorage.getItem(MystoreCountdown.KEY) || '0', 10);
        } catch (e) {}
        if (!end || end <= Date.now()) {
          end = Date.now() + windowMs;
          try {
            localStorage.setItem(MystoreCountdown.KEY, end);
          } catch (e) {}
        }
        return end;
      }

      tick() {
        const end = this.endAt();
        if (Number.isNaN(end)) return;
        const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (left === 0 && this.dataset.end) {
          this.hidden = true;
          clearInterval(this.timer);
          return;
        }
        const pad = (n) => String(n).padStart(2, '0');
        const text = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
        // every digit in its own fixed box, so the stripe never shifts when a 1 becomes a 0
        this.clock.innerHTML = text.replace(/\d/g, '<b>$&</b>');
      }
    }
  );
}
// Tapping a card in Shop the look / You may also like opens Dawn's quick view instead of the product page; the quick
// view has its own "View full details" link. Products with one variant have no quick view and open normally.
document.addEventListener(
  'click',
  (event) => {
    const card = event.target.closest('.mystore-card');
    if (!card || event.defaultPrevented || event.target.closest('.quick-add, quick-add-modal')) return;
    const opener = card.querySelector('modal-opener [data-product-url]');
    if (!opener) return;
    event.preventDefault();
    opener.click();
  },
  true
);

// Colour dots on the product photo pick the colour through the colour box below, and follow it when it changes
document.addEventListener('click', (event) => {
  const dot = event.target.closest('.mystore-dots__dot[data-color-input]');
  if (!dot) return;
  event.preventDefault();
  const input = document.getElementById(dot.dataset.colorInput);
  if (input && !input.checked) input.click();
});
document.addEventListener('change', (event) => {
  const input = event.target.closest('.product-form__input--image input[type="radio"]');
  if (!input) return;
  document.querySelectorAll('.mystore-dots--pick .mystore-dots__dot').forEach((dot) => {
    dot.classList.toggle('is-active', dot.dataset.colorInput === input.id);
  });
});

// Size guide next to the sizes, a bottom sheet on the phone
document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-size-guide-open]');
  if (open) {
    event.preventDefault();
    document.getElementById(open.dataset.sizeGuideOpen)?.showModal();
    return;
  }
  const dialog = event.target.closest('dialog.mystore-size-guide');
  if (dialog && (event.target.closest('[data-size-guide-close]') || event.target === dialog)) dialog.close();
});

if (!customElements.get('mystore-look')) {
  customElements.define(
    'mystore-look',
    class MystoreLook extends HTMLElement {
      connectedCallback() {
        // "You may also like" waits for this, so nothing shows twice
        window.mystoreLookReady = this.load();
      }

      async load() {
        const list = this.querySelector('[data-mystore-list]');
        if (!this.hasAttribute('data-picked')) {
          // complementary, then related recommendations, then the shop's newest products (a new shop has no recommendations yet)
          const urls = ['complementary', 'related'].map((intent) => `${this.dataset.url}&intent=${intent}`);
          if (this.dataset.fallbackUrl) urls.push(this.dataset.fallbackUrl);
          for (const url of urls) {
            const { items } = await mystoreFetchItems(url);
            mystoreAppendItems(list, items, 4 - list.children.length);
            if (list.children.length >= 4) break;
          }
        }
        if (!list.children.length) this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    }
  );
}

if (!customElements.get('mystore-more')) {
  customElements.define(
    'mystore-more',
    class MystoreMore extends HTMLElement {
      page = 0;

      connectedCallback() {
        this.list = this.querySelector('[data-mystore-list]');
        this.button = this.querySelector('[data-more]');
        this.button.addEventListener('click', () => this.loadPage());
        new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.start();
          },
          { rootMargin: '0px 0px 600px 0px' }
        ).observe(this);
      }

      async start() {
        await (window.mystoreLookReady || Promise.resolve());
        if (this.dataset.recsUrl) {
          const { items } = await mystoreFetchItems(this.dataset.recsUrl);
          mystoreAppendItems(this.list, items);
        }
        await this.loadPage();
        if (!this.list.children.length) this.closest('.shopify-section')?.setAttribute('hidden', '');
      }

      // the next page of the shop's products; pages holding only products already shown are skipped
      async loadPage() {
        this.button.setAttribute('aria-busy', 'true');
        let added = 0;
        let hasNext = false;
        do {
          this.page += 1;
          const { items, doc } = await mystoreFetchItems(`${this.dataset.pageUrl}&page=${this.page}`);
          added = mystoreAppendItems(this.list, items);
          hasNext = !!doc?.querySelector('[data-has-next="true"]');
        } while (added === 0 && hasNext);
        this.button.hidden = !hasNext;
        this.button.removeAttribute('aria-busy');
      }
    }
  );
}

// Example reviews: the Filter button opens a row of chips; a chip shows only the matching cards (Benni 3 Nov 2026)
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-rv-filter-toggle]');
  if (toggle) {
    const chips = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!chips) return;
    chips.hidden = !chips.hidden;
    toggle.setAttribute('aria-expanded', String(!chips.hidden));
    return;
  }
  const chip = event.target.closest('[data-rv-filter]');
  if (!chip) return;
  const box = chip.closest('.mystore-rv');
  const filter = chip.dataset.rvFilter;
  box.querySelectorAll('[data-rv-filter]').forEach((other) => {
    other.classList.toggle('is-active', other === chip);
    other.setAttribute('aria-pressed', String(other === chip));
  });
  let shown = 0;
  box.querySelectorAll('.mystore-rv__item').forEach((item) => {
    const match =
      filter === 'all' ||
      (filter === 'photos' && item.dataset.photo === 'true') ||
      (filter === 'fit-true' && item.dataset.fit === 'true') ||
      (filter === 'fit-large' && item.dataset.fit === 'large') ||
      item.dataset.stars === filter;
    item.hidden = !match;
    if (match) shown += 1;
  });
  const none = box.querySelector('.mystore-rv__none');
  if (none) none.hidden = shown > 0;
});

// Quick view bar (Benni 3 Nov 2026): colour, size, Add to cart and "View full details" sit together in a bar pinned
// to the bottom of the pop-up; the photo and price row scroll above it. The pieces stay inside product-info, so
// Dawn's variant updates keep working.
function mystoreQuickBar(modal) {
  const container = modal.querySelector('.product__info-container');
  if (!container || container.querySelector('.mystore-qv-bar')) return;
  const own = (el) => {
    while (el && el.parentElement !== container) el = el.parentElement;
    return el;
  };
  const pieces = [
    own(container.querySelector('variant-selects')),
    own(container.querySelector('.product-form__buttons')),
    modal.querySelector('.product__view-details'),
  ].filter(Boolean);
  if (!pieces.length) return;
  const bar = document.createElement('div');
  bar.className = 'mystore-qv-bar';
  pieces.forEach((piece) => bar.appendChild(piece));
  container.appendChild(bar);
  const info = modal.querySelector('.quick-add-modal__content-info');
  const pad = () => info && (info.style.paddingBottom = bar.offsetHeight + 'px');
  pad();
  new ResizeObserver(pad).observe(bar);
}

document.querySelectorAll('quick-add-modal').forEach((modal) => {
  const content = modal.querySelector('.quick-add-modal__content-info');
  if (!content) return;
  new MutationObserver(() => mystoreQuickBar(modal)).observe(content, { childList: true });
});
